import { Drawer as BaseDrawer } from '@base-ui/react/drawer';
import { FC } from 'react';

import ArrowLeftIcon from '@/assets/svg/ui-elements/arrow-back.svg';
import CloseIcon from '@/assets/svg/ui-elements/close.svg';
import { theme } from '@/design-system/theme';

import Button, { IconButton } from '../Button';
import Typography from '../Typography';
import {
  Actions,
  Header,
  PopupContent,
  RightActions,
  StyledBackdrop,
  StyledContent,
  StyledPopup,
  StyledViewport,
  TitleRow,
} from './Drawer.styled';
import { DrawerProps } from './types';

const Drawer: FC<DrawerProps> = ({
  open,
  onOpenChange,
  onOpenChangeComplete,
  modal,

  children,

  title,
  titleIcon,
  description,
  onBack,
  headerActions,
  showClose = true,
  maxWidth,

  footerContent,
  leftAction,
  primaryAction,
  secondaryAction,
}) => {
  const backButton = onBack && (
    <IconButton
      type="button"
      color="tertiary"
      variant="ghost"
      size="xs"
      aria-label="Back"
      onClick={onBack}
    >
      <ArrowLeftIcon />
    </IconButton>
  );

  const closeButton = showClose && (
    <BaseDrawer.Close
      render={
        <IconButton type="button" color="tertiary" variant="ghost" size="xs" aria-label="Close">
          <CloseIcon />
        </IconButton>
      }
    />
  );

  const leftButton = leftAction && (
    <Button
      color={leftAction.color ?? 'tertiary'}
      variant={leftAction.variant ?? 'outline'}
      size="md"
      onClick={leftAction.onClick}
      disabled={leftAction.disabled}
    >
      {leftAction.children}
    </Button>
  );

  const primaryButton = primaryAction && (
    <Button
      color={primaryAction.color ?? 'primary'}
      variant={primaryAction.variant ?? 'solid'}
      size="md"
      onClick={primaryAction.onClick}
      disabled={primaryAction.disabled}
    >
      {primaryAction.children}
    </Button>
  );

  const secondaryButton = secondaryAction && (
    <Button
      color={secondaryAction.color ?? 'primary'}
      variant={secondaryAction.variant ?? 'outline'}
      size="md"
      onClick={secondaryAction.onClick}
      disabled={secondaryAction.disabled}
    >
      {secondaryAction.children}
    </Button>
  );

  return (
    <BaseDrawer.Root
      open={open}
      onOpenChange={onOpenChange}
      onOpenChangeComplete={onOpenChangeComplete}
      modal={modal}
      swipeDirection="right"
    >
      <BaseDrawer.Portal>
        {modal !== false && <StyledBackdrop />}
        <StyledViewport>
          <StyledPopup $maxWidth={maxWidth}>
            <StyledContent>
              {(title || backButton || closeButton || headerActions || description) && (
                <Header>
                  <TitleRow>
                    {backButton}
                    {titleIcon}
                    {title && (
                      <Typography variant="h6" as="h2" style={{ flex: 1 }}>
                        {title}
                      </Typography>
                    )}
                    {headerActions}
                    {closeButton}
                  </TitleRow>

                  {description && (
                    <Typography
                      variant="body3"
                      color={theme.colors.text.secondary}
                      style={{ overflowWrap: 'anywhere' }}
                    >
                      {description}
                    </Typography>
                  )}
                </Header>
              )}

              <PopupContent>{children}</PopupContent>

              {(footerContent || leftAction || primaryAction || secondaryAction) && (
                <Actions $hasLeftAction={Boolean(footerContent || leftAction)}>
                  {footerContent ?? leftButton}
                  {(primaryAction || secondaryAction) && (
                    <RightActions>
                      {secondaryButton}
                      {primaryButton}
                    </RightActions>
                  )}
                </Actions>
              )}
            </StyledContent>
          </StyledPopup>
        </StyledViewport>
      </BaseDrawer.Portal>
    </BaseDrawer.Root>
  );
};

export default Drawer;

Drawer.displayName = 'Drawer';
